/**
 * The order screens' forms: what they validate, and how their strings become the API's integers.
 *
 * As with manual receipts, amounts are typed as text -- "450", "$12.50" -- and become integer
 * minor units only at the edge, through `parseMoney`. Messages are i18next keys.
 */

import { z } from "zod";
import { parseMoney } from "@/lib/money";
import type { OrderBody, PaymentBody, RefundBody } from "./queries";

function isAmount(value: string): boolean {
  const money = parseMoney(value, "CAD");
  return money !== null && money.minor >= 0;
}

function isPositive(value: string): boolean {
  const money = parseMoney(value, "CAD");
  return money !== null && money.minor > 0;
}

function minor(value: string, currency: string): number {
  const money = parseMoney(value, currency);
  if (money === null) throw new Error(`not an amount: ${value}`);
  return money.minor;
}

// --- a new order --------------------------------------------------------------------------------

export const newOrderSchema = z
  .object({
    business_id: z.string().min(1, "orders.errors.businessRequired"),
    /** An existing customer's id, or "" to create one from `new_customer`. */
    customer_id: z.string(),
    new_customer: z.string().trim().max(160, "orders.errors.tooLong"),
    description: z
      .string()
      .trim()
      .min(1, "orders.errors.descriptionRequired")
      .max(200, "orders.errors.tooLong"),
    due_date: z.string(),
    quote: z
      .string()
      .trim()
      .refine((value) => value === "" || isAmount(value), "orders.errors.amountInvalid"),
    notes: z.string(),
    items: z.array(
      z.object({
        description: z
          .string()
          .trim()
          .min(1, "orders.errors.descriptionRequired")
          .max(200, "orders.errors.tooLong"),
        quantity: z
          .string()
          .trim()
          .refine(
            (value) => /^\d+(\.\d{1,3})?$/.test(value) && Number(value) > 0,
            "orders.errors.quantityInvalid",
          ),
        price: z.string().trim().refine(isAmount, "orders.errors.amountInvalid"),
      }),
    ),
  })
  .refine((values) => values.customer_id !== "" || values.new_customer !== "", {
    path: ["new_customer"],
    message: "orders.errors.customerRequired",
  });

export type NewOrderValues = z.infer<typeof newOrderSchema>;

export const emptyNewOrder: NewOrderValues = {
  business_id: "",
  customer_id: "",
  new_customer: "",
  description: "",
  due_date: "",
  quote: "",
  notes: "",
  items: [],
};

/** The order request, once the customer it is for exists. */
export function toOrderBody(
  values: NewOrderValues,
  customerId: string,
  currency: string,
): OrderBody {
  return {
    customer_id: customerId,
    description: values.description,
    notes: values.notes,
    ...(values.due_date ? { due_date: values.due_date } : {}),
    ...(values.quote ? { quoted_total_minor: minor(values.quote, currency) } : {}),
    items: values.items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      unit_price_minor: minor(item.price, currency),
    })),
  };
}

// --- money --------------------------------------------------------------------------------------

export const paymentSchema = z.object({
  direction: z.enum(["in", "out"]),
  method_id: z.string().min(1, "orders.errors.methodRequired"),
  amount: z.string().trim().refine(isPositive, "orders.errors.amountPositive"),
  paid_on: z.string().min(1, "orders.errors.dateRequired"),
  note: z.string(),
});

export type PaymentValues = z.infer<typeof paymentSchema>;

export function toPaymentBody(values: PaymentValues, currency: string): PaymentBody {
  return {
    direction: values.direction,
    method_id: values.method_id,
    amount_minor: minor(values.amount, currency),
    paid_on: values.paid_on,
    note: values.note,
  };
}

export function toRefundBody(values: PaymentValues, currency: string): RefundBody {
  return {
    method_id: values.method_id,
    amount_minor: minor(values.amount, currency),
    paid_on: values.paid_on,
    note: values.note,
  };
}

// --- an order's details, edited -----------------------------------------------------------------

export const orderDetailsSchema = z.object({
  description: z
    .string()
    .trim()
    .min(1, "orders.errors.descriptionRequired")
    .max(200, "orders.errors.tooLong"),
  due_date: z.string(),
  quote: z
    .string()
    .trim()
    .refine((value) => value === "" || isAmount(value), "orders.errors.amountInvalid"),
  notes: z.string(),
});

export type OrderDetailsValues = z.infer<typeof orderDetailsSchema>;

/** Every field is sent, so an emptied date or quote is cleared. */
export function toOrderPatch(values: OrderDetailsValues, currency: string) {
  return {
    description: values.description,
    due_date: values.due_date || null,
    quoted_total_minor: values.quote ? minor(values.quote, currency) : null,
    notes: values.notes,
  };
}
